import { Input } from "antd";
import restClient from "api/restClient";
import { Button } from "components/Button/Button";
import { IconCollection } from "components/Icon/IconCollection";
import { Markdown } from "components/Form/components/Markdown/Markdown";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import { BlockingNavigationModal } from "components/Prompt/BlockingNavigationModal";
import { NAVIGATION_PRIORITIES } from "components/Prompt/NavigationPromptContext";
import { SpinnerAnimation } from "components/SpinnerAnimation/SpinnerAnimation";
import { TimeAgoWithDateTimeFormatter } from "components/TimeAgo/TimeAgoWithDateTimeFormatter";
import { DocsInfoTooltip } from "components/Tooltip/DocsInfoTooltip";
import { FeConfigType } from "config/config";
import { getUserData } from "containers/Auth/Auth.selectors";
import { actions as resourcesActions } from "containers/Resources/Resources.reducer";
import { useMigrateOnSaveParams } from "containers/Resources/screens/ResourcesSingle/useCanCommitOnSave";
import { Location } from "history";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { actions as dataStoreAction } from "utils/DataStore/DataStore.reducer";
import strings from "utils/strings";
import { getIri, getSearchObj } from "utils/utils";
import styles from "./CommentsPanel.module.scss";

/**
 * One row returned by the generic `record_comment_plain_list` GraphQL query.
 *
 * Typed locally rather than importing from `graphql/generated` because the
 * generated types are (re)produced by the build-time codegen against the
 * deployed backend schema; this keeps local typecheck green while the panel
 * is authored ahead of the first regeneration.
 */
interface RecordCommentRow {
    id: string;
    body: string;
    author_identity_id: string | null;
    author_identity_id_identity: { name: string } | null;
    assignee_identity_id: string | null;
    parent_comment_id: string | null;
    resolved_at: string | null;
    resolved_by: string | null;
    created_at: string;
}

interface CommentsPanelProps {
    id: string;
    isDisabled: boolean;
    setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
    resourceName?: FeConfigType;
    /**
     * Bumped by the parent record screen's manual Refresh. Added to the comment
     * fetch effect deps so a record refresh also re-fetches the comment list,
     * keeping it in sync with the comments-tab badge count (which rides the
     * record body re-fetch). See operator-task-fe-record-refresh-stale-comments.
     */
    refreshNonce?: number;
    /**
     * Same-record / in-page navigation escape hatch, forwarded to the pending-
     * comment navigation guard. Lets same-record URL canonicalizations (the
     * `history.replace` that strips a sub-page flag) through without prompting,
     * so the guard fires only on genuine navigation away — reusing the exact
     * predicate the parent record form uses (see isSameRecordViewNavigation in
     * ResourcesSingle) rather than a parallel check.
     */
    allowNavigation?: (nextLocation: Location) => boolean;
}

const authorLabel = (comment: RecordCommentRow, currentIdentityId?: string): string => {
    const authorIdentityId = comment.author_identity_id;
    if (!authorIdentityId) {
        return "System";
    }
    if (currentIdentityId && authorIdentityId === currentIdentityId) {
        return "You";
    }
    // Prefer the resolved identity name; fall back to a short id when the
    // relationship is unreadable (RBAC) or absent.
    const resolvedName = comment.author_identity_id_identity?.name;
    if (resolvedName) {
        return resolvedName;
    }
    return `Identity ${authorIdentityId.slice(0, 8)}`;
};

export const CommentsPanel = ({
    id,
    isDisabled,
    setHasUnsavedChanges,
    resourceName,
    refreshNonce,
    allowNavigation,
}: CommentsPanelProps) => {
    const dispatch = useDispatch();
    const history = useHistory();

    const key = `recordCommentList-${id}`;

    const [isLoading, setLoading] = useState(false);
    // Dedicated in-flight flag for the *post comment* request, kept separate
    // from the panel-wide isLoading (list fetch / delete) so the submit button
    // reflects submission state precisely. Drives both the button's pending
    // affordance (spinner) and its disabled state, and guards postComment
    // against re-entry (double-click / future Ctrl+Enter) posting a duplicate.
    const [isSubmitting, setSubmitting] = useState(false);
    const [draft, setDraft] = useState("");

    // A non-empty (non-whitespace) draft is a pending comment that would be lost
    // on navigate-away. Drives both the in-page tab-switch guard (via
    // setHasUnsavedChanges) and the route-change / beforeunload guard below.
    const hasPendingComment = draft.trim().length > 0;

    const userData = useSelector(getUserData);
    const currentIdentityId: string | undefined = userData?.identity_id;

    const getRecordCommentListSelector = (state) => state?.dataStore?.[key];
    const commentList: RecordCommentRow[] = useSelector(getRecordCommentListSelector)
        ?.record_comment_plain_list;

    // A non-empty draft counts as unsaved changes so tab-switching is guarded.
    useEffect(() => {
        setHasUnsavedChanges(hasPendingComment);
    }, [hasPendingComment, setHasUnsavedChanges]);

    const getData = useCallback(() => {
        setLoading(true);
        const filterStr = JSON.stringify(getSearchObj({ field: "record_id", value: id, op: "eq" }));
        new Promise((resolve, reject) => {
            dispatch({
                promise: { resolve, reject },
                type: dataStoreAction.START_FETCH_DATA,
                key,
                params: {
                    query: `query($filter: String, $order: String, $limit: Int, $offset: Int) {
                        record_comment_plain_list(filter:$filter,order:$order,limit:$limit,offset:$offset){
                            id
                            body
                            author_identity_id
                            author_identity_id_identity { name }
                            assignee_identity_id
                            parent_comment_id
                            resolved_at
                            resolved_by
                            created_at
                        },
                    }`,
                    variables: { filter: filterStr, order: "+created_at", offset: 0 },
                },
            });
        })
            .then((data: any) => {
                const count = data?.record_comment_plain_list?.length;
                dispatch({
                    type: resourcesActions.STORE_RESOURCE_DATA_WITH_PARTIAL_UPDATE,
                    iri: getIri(resourceName, id),
                    response: {
                        record_comment_list_record_id: {
                            record_comment_count: count,
                        },
                    },
                });
            })
            .catch((error) => error)
            .finally(() => setLoading(false));
    }, [dispatch, id, key, resourceName]);

    // Re-fetch on mount / id change AND whenever the parent bumps refreshNonce
    // (manual record Refresh), so the list stays in sync with the badge count.
    useEffect(() => {
        if (id) {
            getData();
        }
    }, [getData, id, refreshNonce]);

    const migrateOnSaveParams = useMigrateOnSaveParams();
    const params = useMemo(
        () => ({ commit: false, ...migrateOnSaveParams }),
        [migrateOnSaveParams]
    );

    const postComment = useCallback(() => {
        const body = draft.trim();
        // Re-entry guard: never fire a second create while one is already in
        // flight (a rapid double-click, or a future Ctrl+Enter path) — this is
        // what previously posted the same comment multiple times.
        if (!body || isSubmitting) {
            return;
        }
        setSubmitting(true);
        restClient
            .post(
                "/graphql",
                {
                    query: `mutation create_record_comment($record_id: String!, $body: String!) {
                        create_record_comment(body: {record_id: $record_id, body: $body}) {
                            id
                        }
                    }`,
                    variables: { record_id: id, body },
                },
                { params }
            )
            .then(() => {
                setDraft("");
                getData();
            })
            .catch((error) =>
                NotificationService.error(
                    <RestErrorNotification
                        titleOverride="Sorry, couldn't post comment"
                        error={error}
                    />
                )
            )
            .finally(() => setSubmitting(false));
    }, [draft, id, params, getData, isSubmitting]);

    const handleDraftKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
            // Ctrl+Enter / Cmd+Enter posts the comment — the conventional
            // submit-multiline-text shortcut. Plain Enter keeps its default
            // behaviour (newline in the multi-line composer). postComment
            // itself no-ops on an empty draft or an in-flight submit, so no
            // extra guarding is needed here; and a disabled composer never
            // receives keydown events.
            if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
                event.preventDefault();
                postComment();
            }
        },
        [postComment]
    );

    const deleteComment = useCallback(
        (commentId: string) => {
            setLoading(true);
            restClient
                .post(
                    "/graphql",
                    {
                        query: `mutation delete_record_comment($id: String!){
                            delete_record_comment(id: $id)
                        }`,
                        variables: { id: commentId },
                    },
                    { params }
                )
                .then(() => getData())
                .catch((error) =>
                    NotificationService.error(
                        <RestErrorNotification
                            titleOverride="Sorry, couldn't delete comment"
                            error={error}
                        />
                    )
                )
                .finally(() => setLoading(false));
        },
        [params, getData]
    );

    const comments = commentList ?? [];

    return (
        <div className={styles.commentsPanel}>
            {/*
             * Guard the pending comment against navigate-away (in-app route
             * change + browser tab close/refresh via beforeunload). Reuses the
             * shared unsaved-changes machinery — the same BlockingNavigationModal
             * the parent record form and the metadata tab use — rather than a
             * parallel mechanism. An empty/whitespace-only composer does not
             * block (hasPendingComment is false).
             */}
            <BlockingNavigationModal
                navigate={history.push}
                shouldBlockNavigation={hasPendingComment}
                titleText={strings.navigateAwayUnsavedCommentPrompt}
                priority={NAVIGATION_PRIORITIES.COMMENT_FORM}
                allowNavigation={allowNavigation}
            />
            <SpinnerAnimation isLoading={isLoading} className={styles.spinnerAnimation} />
            {comments.length === 0 && !isLoading ? (
                <div className={styles.emptyState}>No comments yet.</div>
            ) : (
                <div className={styles.commentList}>
                    {comments.map((comment) => (
                        <div className={styles.comment} key={comment.id}>
                            <div className={styles.commentHeader}>
                                <span className={styles.author}>
                                    {authorLabel(comment, currentIdentityId)}
                                </span>
                                <span className={styles.timestamp}>
                                    <TimeAgoWithDateTimeFormatter datetime={comment.created_at} />
                                </span>
                                <div className={styles.spacer} />
                                <Button
                                    icon={IconCollection.deleteOutline}
                                    variation="icon"
                                    onClick={() => deleteComment(comment.id)}
                                    disabled={isDisabled}
                                />
                            </div>
                            <Markdown
                                markdownString={comment.body ?? ""}
                                canCopy={false}
                                className={styles.commentBody}
                            />
                        </div>
                    ))}
                </div>
            )}
            <div className={styles.newComment}>
                <Input.TextArea
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    onKeyDown={handleDraftKeyDown}
                    placeholder="Write a comment (Markdown supported)…"
                    autoSize={{ minRows: 3, maxRows: 12 }}
                    disabled={isDisabled}
                />
                <div className={styles.newCommentActions}>
                    <Button
                        label="Comment"
                        title="Post comment (Ctrl+Enter / ⌘+Enter)"
                        onClick={postComment}
                        variation="primary"
                        size="small"
                        loading={isSubmitting}
                        disabled={draft.trim().length === 0 || isDisabled}
                    />
                    <DocsInfoTooltip
                        title="Record comments"
                        urlPath="/docs/accessing-and-manipulating-records#record-comments"
                    />
                </div>
            </div>
        </div>
    );
};
